/**
 * @fileoverview Slider component: standard (finite) and infinite modes with
 * optional auto-rotate, swipe, wheel, dots, and thumbnails.
 */

import { logWarn, showErrorOnElement } from './errors.js';

/** @constant {number} Default auto-rotate interval (ms) */
const DEFAULT_AUTO_ROTATE_MS = 3000;

/** @constant {number} Min horizontal delta to treat wheel as horizontal (px) */
const WHEEL_DELTA_THRESHOLD = 10;

/** @constant {number} Cooldown after wheel navigation (ms) */
const WHEEL_COOLDOWN_MS = 200;

/** @constant {number} Min move (px) to consider touch as swipe intent */
const SWIPE_INTENT_THRESHOLD_PX = 30;

/** @constant {number} Min distance (px) to trigger next/prev on swipe */
const SWIPE_ACTION_THRESHOLD_PX = 50;

/** @constant {number} Resize debounce delay (ms) */
const RESIZE_DEBOUNCE_MS = 100;

/** Data attribute used to prevent re-initialization */
const DATA_INITIALIZED = 'initialized';

/**
 * @typedef {Object} SliderState
 * @property {Element} slider
 * @property {NodeListOf<Element>} initialSlides
 * @property {Element} prevButton
 * @property {Element} nextButton
 * @property {boolean} isInfinite
 * @property {boolean} isStandard
 * @property {Element | null} autoRotateEl
 * @property {number} autoRotateMs
 * @property {number} currentIndex
 * @property {NodeListOf<Element>} slides
 * @property {number} slidesCount
 * @property {number} slideWidth
 * @property {number} originalCount
 * @property {number} firstOriginalIndex
 * @property {number} lastOriginalIndex
 * @property {number | null} wrapNextCloneIndex
 * @property {number | null} wrapPrevCloneIndex
 * @property {string | null} wrapJumpPending
 * @property {(() => void) | null} [resizeTimeoutId]
 * @property {number} touchStartX
 * @property {boolean} isSwiping
 * @property {boolean} isWheelCooldown
 * @property {((e: TransitionEvent) => void) | null} [_wrapTransitionBound]
 */

/**
 * Initializes all slider instances in the document.
 * Each element with [vdx-slider-slider] is set up once; re-calling is safe.
 */
export function initSlider() {
    const sliders = document.querySelectorAll('[vdx-slider-slider]');
    sliders.forEach((slider) => {
        if (slider.dataset[DATA_INITIALIZED]) return;
        slider.dataset[DATA_INITIALIZED] = 'true';

        const state = createSliderState(slider);
        if (!state) return;

        setupSlider(state);
        attachEventListeners(state);
        setupNavigationDots(state);
        setupThumbnails(state);
        setupAutoRotate(state);
    });
}

/**
 * Validates DOM and builds state for one slider. Returns null if invalid.
 * @param {Element} slider - Root slider element
 * @returns {SliderState | null}
 */
function createSliderState(slider) {
    const initialSlides = slider.querySelectorAll('[vdx-slider-slide]');
    if (initialSlides.length === 0) {
        const msg = 'No elements with [vdx-slider-slide] found. Add at least one child with attribute vdx-slider-slide.';
        logWarn(msg, { module: 'slider' });
        showErrorOnElement(slider, msg);
        return null;
    }

    const prevButton = slider.querySelector('[vdx-slider-prev]');
    const nextButton = slider.querySelector('[vdx-slider-next]');
    if (!prevButton || !nextButton) {
        const missing = [!prevButton && 'vdx-slider-prev', !nextButton && 'vdx-slider-next'].filter(Boolean).join(', ');
        const msg = `[${missing}] not found. Add one element with vdx-slider-prev and one with vdx-slider-next inside the slider.`;
        logWarn(msg, { module: 'slider' });
        showErrorOnElement(slider, msg);
        return null;
    }

    const mode = slider.getAttribute('vdx-slider-slider') ?? '';
    const isInfinite = mode === 'infinite';
    const isStandard = mode === '' || mode === 'standard';

    if (!isStandard && !isInfinite) {
        const msg = `vdx-slider-slider value must be "infinite" or "standard". Got: "${mode || '(empty)'}".`;
        logWarn(msg, { module: 'slider', context: { mode } });
        showErrorOnElement(slider, msg);
        return null;
    }

    // Set logical index on each slide
    initialSlides.forEach((slide, index) => {
        slide.setAttribute('vdx-slider-slide', String(index));
    });

    const autoRotateAttr = slider.getAttribute('vdx-slider-autorotate');
    const autoRotateMs = autoRotateAttr
        ? parseInt(autoRotateAttr, 10) || DEFAULT_AUTO_ROTATE_MS
        : 0;

    return {
        slider,
        initialSlides,
        prevButton,
        nextButton,
        isInfinite,
        isStandard,
        autoRotateEl: slider.hasAttribute('vdx-slider-autorotate') ? slider : null,
        autoRotateMs,
        currentIndex: 0,
        slides: null,
        slidesCount: 0,
        slideWidth: 0,
        originalCount: initialSlides.length,
        firstOriginalIndex: 0,
        lastOriginalIndex: 0,
        wrapNextCloneIndex: null,
        wrapPrevCloneIndex: null,
        wrapJumpPending: null,
        resizeTimeoutId: null,
        touchStartX: 0,
        isSwiping: false,
        isWheelCooldown: false,
    };
}

/**
 * Sets up DOM (clones if infinite), indices, and initial position.
 * @param {SliderState} state
 */
function setupSlider(state) {
    const { slider, initialSlides, isInfinite, prevButton, nextButton } = state;

    if (isInfinite) {
        setupInfiniteClones(state);
        state.currentIndex = initialSlides.length;
    } else {
        setStandardButtonState(prevButton, nextButton, initialSlides.length);
    }

    state.slides = slider.querySelectorAll('[vdx-slider-slide]');
    state.slidesCount = state.slides.length;

    const N = state.originalCount;
    state.firstOriginalIndex = isInfinite ? N : 0;
    state.lastOriginalIndex = isInfinite ? 2 * N - 1 : state.slidesCount - 1;
    state.wrapNextCloneIndex = isInfinite ? 2 * N : null;
    state.wrapPrevCloneIndex = isInfinite ? N - 1 : null;

    state.slideWidth = getSlideWidth(state);
    setActiveSlide(state, state.currentIndex);

    if (isInfinite) {
        setStripPosition(state, state.currentIndex, true);
        requestAnimationFrame(() => {
            state.slides.forEach((s) => { s.style.transition = ''; });
        });
    }

    updateButtonsAndDots(state, state.currentIndex);
    window.addEventListener('resize', createResizeHandler(state));
}

/**
 * @param {SliderState} state
 */
function setupInfiniteClones(state) {
    const { slider, initialSlides } = state;
    const firstSlide = slider.querySelector('[vdx-slider-slide]');
    const container = firstSlide?.parentElement;
    if (!container) {
        const msg = 'No parent element for [vdx-slider-slide] found. Wrap slides in a container (e.g. a div) inside the slider.';
        logWarn(msg, { module: 'slider' });
        showErrorOnElement(state.slider, msg);
        return;
    }

    const N = initialSlides.length;
    for (let i = N - 1; i >= 0; i--) {
        const clone = initialSlides[i].cloneNode(true);
        clone.setAttribute('vdx-slider-clone', 'leading');
        clone.setAttribute('vdx-slider-slide', String(i));
        container.insertBefore(clone, container.firstChild);
    }
    for (let i = 0; i < N; i++) {
        const clone = initialSlides[i].cloneNode(true);
        clone.setAttribute('vdx-slider-clone', 'trailing');
        clone.setAttribute('vdx-slider-slide', String(i));
        container.appendChild(clone);
    }
    state.prevButton.setAttribute('aria-disabled', 'false');
    state.nextButton.setAttribute('aria-disabled', 'false');
}

/**
 * @param {HTMLButtonElement} prev
 * @param {HTMLButtonElement} next
 * @param {number} slideCount
 */
function setStandardButtonState(prev, next, slideCount) {
    prev.classList.add('vdx--inactive');
    prev.setAttribute('aria-disabled', 'true');
    next.setAttribute('aria-disabled', slideCount <= 1 ? 'true' : 'false');
    if (slideCount <= 1) next.classList.add('vdx--inactive');
}

/**
 * @param {SliderState} state
 * @returns {number}
 */
function getSlideWidth(state) {
    const slide = state.slides[0];
    const container = slide?.parentElement;
    if (!slide || !container) return 0;
    const gap = parseInt(getComputedStyle(container).gap, 10) || 0;
    const style = getComputedStyle(slide);
    const marginLeft = parseInt(style.marginLeft, 10) || 0;
    const marginRight = parseInt(style.marginRight, 10) || 0;
    return slide.offsetWidth + marginLeft + marginRight + gap;
}

/**
 * @param {SliderState} state
 * @returns {() => void}
 */
function createResizeHandler(state) {
    let timeoutId = null;
    const handler = () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            state.slideWidth = getSlideWidth(state);
            timeoutId = null;
        }, RESIZE_DEBOUNCE_MS);
    };
    state.resizeTimeoutId = () => timeoutId && clearTimeout(timeoutId);
    return handler;
}

/**
 * @param {SliderState} state
 * @param {number} index
 */
function setActiveSlide(state, index) {
    const slide = state.slides[index];
    if (!slide) return;
    state.slides.forEach((s) => {
        s.classList.remove('vdx--active');
        s.querySelectorAll('*').forEach((el) => el.classList.remove('vdx--active'));
    });
    slide.classList.add('vdx--active');
    slide.querySelectorAll('*').forEach((el) => el.classList.add('vdx--active'));
}

/**
 * @param {SliderState} state
 * @param {number} index
 * @param {boolean} [noTransition]
 */
function setStripPosition(state, index, noTransition = false) {
    const { slides, slideWidth } = state;
    const tx = -index * slideWidth;
    slides.forEach((slide) => {
        slide.style.transition = noTransition ? 'none' : '';
        slide.style.transform = `translateX(${tx}px)`;
    });
    if (noTransition) void state.slider.offsetWidth;
}

/**
 * @param {SliderState} state
 * @param {number} physicalIndex
 * @returns {number}
 */
function getLogicalIndex(state, physicalIndex) {
    const slide = state.slides[physicalIndex];
    if (!slide) return 0;
    const val = parseInt(slide.getAttribute('vdx-slider-slide'), 10);
    return Number.isNaN(val) ? 0 : val;
}

/**
 * @param {SliderState} state
 * @param {number} index
 */
function updateButtonsAndDots(state, index) {
    const { isInfinite, prevButton, nextButton, slidesCount, slider } = state;
    if (!isInfinite) {
        const atStart = index === 0;
        const atEnd = index === slidesCount - 1;
        prevButton.classList[atStart ? 'add' : 'remove']('vdx--inactive');
        prevButton.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        nextButton.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
        nextButton.classList[atEnd ? 'add' : 'remove']('vdx--inactive');
    }
    const logical = getLogicalIndex(state, index);
    slider.querySelectorAll('[vdx-slider-dot]').forEach((dot, i) => {
        dot.classList.toggle('vdx--active', i === logical);
    });
    slider.querySelectorAll('[vdx-slider-thumbnail]').forEach((thumb, i) => {
        thumb.classList.toggle('vdx--active', i === logical);
    });
}

/**
 * Instant jump (no transition). Used for infinite wrap reset.
 * @param {SliderState} state
 * @param {number} targetIndex
 */
function applyJump(state, targetIndex) {
    const { slides, slideWidth } = state;
    slides.forEach((slide) => {
        slide.style.transition = 'none';
        slide.style.transform = `translateX(${-targetIndex * slideWidth}px)`;
        slide.classList.remove('vdx--active');
        slide.querySelectorAll('*').forEach((el) => el.classList.remove('vdx--active'));
    });
    const target = slides[targetIndex];
    if (target) {
        target.classList.add('vdx--active');
        target.querySelectorAll('*').forEach((el) => el.classList.add('vdx--active'));
    }
    void state.slider.offsetWidth;
}

/**
 * Called when wrap animation ends; jumps to the corresponding original index.
 * @param {TransitionEvent} event
 * @param {SliderState} state
 */
function onWrapTransitionEnd(event, state) {
    if (event.propertyName !== 'transform') return;
    if (!state.wrapJumpPending) return;
    const targetIndex = state.wrapJumpPending === 'toFirst'
        ? state.firstOriginalIndex
        : state.lastOriginalIndex;
    state.wrapJumpPending = null;
    if (state._wrapTransitionBound) {
        state.slides[0].removeEventListener('transitionend', state._wrapTransitionBound);
        state._wrapTransitionBound = null;
    }
    applyJump(state, targetIndex);
    state.currentIndex = targetIndex;
    updateButtonsAndDots(state, targetIndex);
}

/**
 * Moves slider to the given physical index (with transition or jump as needed).
 * @param {SliderState} state
 * @param {number} index
 */
function moveSlider(state, index) {
    const {
        isInfinite,
        firstOriginalIndex,
        lastOriginalIndex,
        wrapNextCloneIndex,
        wrapPrevCloneIndex,
        slides,
        slideWidth,
    } = state;

    const isWrapPrev = isInfinite && state.currentIndex === firstOriginalIndex && index === lastOriginalIndex;
    const isWrapNext = isInfinite && state.currentIndex === lastOriginalIndex && index === firstOriginalIndex;

    if (isWrapPrev) {
        state.wrapJumpPending = 'toLast';
        index = wrapPrevCloneIndex;
    } else if (isWrapNext) {
        state.wrapJumpPending = 'toFirst';
        index = wrapNextCloneIndex;
    } else {
        state.wrapJumpPending = null;
        if (state._wrapTransitionBound) {
            state.slides[0].removeEventListener('transitionend', state._wrapTransitionBound);
            state._wrapTransitionBound = null;
        }
    }

    if (state.wrapJumpPending) {
        state._wrapTransitionBound = (e) => onWrapTransitionEnd(e, state);
        state.slides[0].addEventListener('transitionend', state._wrapTransitionBound);
    }

    const tx = -index * slideWidth;
    slides.forEach((slide, i) => {
        slide.style.transition = '';
        slide.style.transform = `translateX(${tx}px)`;
        slide.classList.remove('vdx--active');
        slide.querySelectorAll('*').forEach((el) => el.classList.remove('vdx--active'));
        if (i === index) {
            slide.classList.add('vdx--active');
            slide.querySelectorAll('*').forEach((el) => el.classList.add('vdx--active'));
        }
    });

    state.currentIndex = index;
    updateButtonsAndDots(state, index);
}

/**
 * @param {SliderState} state
 * @returns {number}
 */
function getNextIndex(state) {
    if (state.isInfinite && state.currentIndex === state.lastOriginalIndex) {
        return state.firstOriginalIndex;
    }
    return (state.currentIndex + 1) % state.slidesCount;
}

/**
 * @param {SliderState} state
 * @returns {number}
 */
function getPrevIndex(state) {
    if (state.isInfinite && state.currentIndex === state.firstOriginalIndex) {
        return state.lastOriginalIndex;
    }
    return (state.currentIndex - 1 + state.slidesCount) % state.slidesCount;
}

/**
 * @param {SliderState} state
 */
function attachEventListeners(state) {
    const { slider, prevButton, nextButton } = state;

    const handleButtonClick = (isPrev) => {
        if (isPrev && prevButton.getAttribute('aria-disabled') === 'true') return;
        if (!isPrev && nextButton.getAttribute('aria-disabled') === 'true') return;
        moveSlider(state, isPrev ? getPrevIndex(state) : getNextIndex(state));
    };

    prevButton.addEventListener('click', () => handleButtonClick(true));
    nextButton.addEventListener('click', () => handleButtonClick(false));

    slider.addEventListener('touchstart', (e) => {
        state.touchStartX = e.touches[0]?.clientX ?? 0;
    }, { passive: true });

    slider.addEventListener('touchmove', (e) => {
        if (state.touchStartX == null) return;
        const x = e.touches[0]?.clientX;
        if (x != null && Math.abs(state.touchStartX - x) > SWIPE_INTENT_THRESHOLD_PX) {
            state.isSwiping = true;
        }
    }, { passive: true });

    slider.addEventListener('touchend', (e) => {
        const start = state.touchStartX;
        state.touchStartX = 0;
        if (start == null || !state.isSwiping) {
            state.isSwiping = false;
            return;
        }
        const end = e.changedTouches?.[0]?.clientX;
        if (end == null) {
            state.isSwiping = false;
            return;
        }
        const distance = start - end;
        if (distance > SWIPE_ACTION_THRESHOLD_PX) {
            moveSlider(state, getNextIndex(state));
        } else if (distance < -SWIPE_ACTION_THRESHOLD_PX) {
            moveSlider(state, getPrevIndex(state));
        }
        e.preventDefault();
        state.isSwiping = false;
    });

    slider.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaX) <= WHEEL_DELTA_THRESHOLD) return;
        if (state.isWheelCooldown) return;
        state.isWheelCooldown = true;
        const next = e.deltaX > 0;
        moveSlider(state, next ? getNextIndex(state) : getPrevIndex(state));
        setTimeout(() => { state.isWheelCooldown = false; }, WHEEL_COOLDOWN_MS);
    });
}

/**
 * @param {SliderState} state
 */
function setupNavigationDots(state) {
    const { slider, initialSlides, isInfinite, firstOriginalIndex } = state;
    const dotTemplate = slider.querySelector('[vdx-slider-dot]');
    const nav = slider.querySelector('[vdx-slider-nav]');
    if (!nav || !dotTemplate) return;

    nav.innerHTML = '';
    initialSlides.forEach((_, index) => {
        const dot = dotTemplate.cloneNode(true);
        dot.setAttribute('data-slide-index', String(index));
        dot.addEventListener('click', () => {
            const targetIndex = isInfinite ? firstOriginalIndex + index : index;
            moveSlider(state, targetIndex);
        });
        nav.appendChild(dot);
    });
    const firstDot = nav.querySelector('[data-slide-index="0"]');
    if (firstDot) firstDot.classList.add('vdx--active');
}

/**
 * @param {SliderState} state
 */
function setupThumbnails(state) {
    const { slider, initialSlides, isInfinite, firstOriginalIndex } = state;
    const thumbs = slider.querySelectorAll('[vdx-slider-thumbnail]');
    if (!thumbs.length) return;

    initialSlides.forEach((_, index) => {
        const thumb = thumbs[index];
        if (!thumb) return;
        thumb.setAttribute('data-slide-index', String(index));
        thumb.addEventListener('click', () => {
            const targetIndex = isInfinite ? firstOriginalIndex + index : index;
            moveSlider(state, targetIndex);
        });
    });
    if (thumbs[0]) thumbs[0].classList.add('vdx--active');
}

/**
 * @param {SliderState} state
 */
function setupAutoRotate(state) {
    const { autoRotateEl, autoRotateMs } = state;
    if (!autoRotateEl || autoRotateMs <= 0) return;

    let intervalId = setInterval(() => moveSlider(state, getNextIndex(state)), autoRotateMs);
    const stop = () => {
        clearInterval(intervalId);
        intervalId = null;
    };
    const start = () => {
        if (intervalId) return;
        intervalId = setInterval(() => moveSlider(state, getNextIndex(state)), autoRotateMs);
    };
    state.slider.addEventListener('mouseover', stop);
    state.slider.addEventListener('click', stop);
    state.slider.addEventListener('mouseout', start);
}
